import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import styled, { keyframes } from 'styled-components'
import { toast } from 'react-toastify'
import { SkeletonRows } from 'components/Skeleton'
import SearchableSelect from 'components/SearchableSelect'
import { RAG } from 'features/dashboard/charts/scoreColour'
import AutoAwesomeRoundedIcon    from '@mui/icons-material/AutoAwesomeRounded'
import InfoOutlinedIcon          from '@mui/icons-material/InfoOutlined'
import CheckRoundedIcon          from '@mui/icons-material/CheckRounded'
import SearchRoundedIcon         from '@mui/icons-material/SearchRounded'
import ChevronRightRoundedIcon   from '@mui/icons-material/ChevronRightRounded'
import OpenInNewRoundedIcon      from '@mui/icons-material/OpenInNewRounded'
import WarningAmberRoundedIcon   from '@mui/icons-material/WarningAmberRounded'
import DescriptionRoundedIcon    from '@mui/icons-material/DescriptionRounded'
import TaskAltRoundedIcon        from '@mui/icons-material/TaskAltRounded'
import GavelRoundedIcon          from '@mui/icons-material/GavelRounded'
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded'
import AccountBalanceRoundedIcon  from '@mui/icons-material/AccountBalanceRounded'
import AddRoundedIcon             from '@mui/icons-material/AddRounded'
import TuneRoundedIcon            from '@mui/icons-material/TuneRounded'
import CustomiseObligationModal   from 'features/compliance/CustomiseObligationModal'
import {
  getObligationSetup,
  prefillObligations,
  getSetupProgress,
  bulkUpdateObligations,
} from 'services/checklistService'
import { markProgrammeReviewed } from 'services/ProgrammeService'
import TaskVsEvidenceExplainer from 'features/compliance/TaskVsEvidenceExplainer'
import AiActionExplainer from 'components/AiActionExplainer'
import {
  CADENCE_PRESETS,
  CRITICALITY_LEVELS,
  SCOPE_OPTIONS,
  cadenceLabel,
  criticalityTint,
} from 'features/compliance/obligationConstants'
import AddProgrammeObligationModal from 'features/compliance/AddProgrammeObligationModal'
import { createPortal } from 'react-dom'
import { PRIVATE_PATHS, redirect } from 'utils/paths'

const ObligationSetupTable = ({ bpId, bpName }) => {
  const [loading, setLoading]     = useState(true)
  const [rows, setRows]           = useState([])
  const [original, setOriginal]   = useState({})
  const [units, setUnits]         = useState([])
  const [tags, setTags]           = useState([])
  const [domains, setDomains]     = useState([])
  const [fileTypes, setFileTypes] = useState([])
  const [filterDomain, setFilterDomain] = useState('all')
  const [filterApplies, setFilterApplies] = useState('all')
  const [filterText, setFilterText] = useState('')
  const [saving, setSaving]       = useState(false)
  const [prefilling, setPrefilling] = useState(false)
  const [expanded, setExpanded]   = useState(new Set())
  const [groupBy, setGroupBy]     = useState('legislation')
  const [collapsedLegs, setCollapsedLegs] = useState(new Set())
  const [addingObligation, setAddingObligation] = useState(false)
  const [customiseTarget, setCustomiseTarget] = useState(null)
  const [unitsTarget, setUnitsTarget] = useState(null)

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const res = await getObligationSetup(bpId)
      const payload = res?.data?.data || {}
      const incoming = (payload.rows || []).map(r => ({ ...r, _dirty: false }))
      setRows(prev => silent
        ? incoming.map(row => {
            const current = prev.find(p => p.id === row.id)
            return current?._dirty ? current : row
          })
        : incoming)
      setOriginal(Object.fromEntries(incoming.map(r => [r.id, r])))
      setUnits(payload.units || [])
      setTags(payload.tags || [])
      setDomains(payload.domains || [])
      setFileTypes(payload.fileTypes || [])
    } catch (e) {
      if (!silent) toast.error('Could not load obligations.')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [bpId])

  useEffect(() => { load() }, [load])

  const pollRef = useRef(null)

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  useEffect(() => stopPolling, [stopPolling])

  const autoPrefilledRef = useRef(false)
  useEffect(() => {
    if (autoPrefilledRef.current) return
    if (!loading && rows.length > 0 && rows.every(r => !r.aiPrefilledAt)) {
      autoPrefilledRef.current = true
      void runPrefill(true)
    }
  }, [loading])

  const startPolling = silent => {
    stopPolling()
    const startedAt = Date.now()
    let lastClassified = null
    pollRef.current = setInterval(async () => {
      if (Date.now() - startedAt >= 120000) {
        stopPolling()
        setPrefilling(false)
        await load({ silent: true })
        return
      }
      try {
        const res = await getSetupProgress(bpId)
        const prefill = res?.data?.data?.prefill || {}
        const classified = prefill.classified ?? 0
        const total = prefill.total ?? 0
        if (total > 0 && classified >= total) {
          stopPolling()
          setPrefilling(false)
          await load({ silent: true })
          if (!silent) toast.success('AI applicability check finished. Obligations updated below.')
          return
        }
        if (lastClassified !== null && classified !== lastClassified) {
          await load({ silent: true })
        }
        lastClassified = classified
      } catch {}
    }, 5000)
  }

  const runPrefill = async (silent = false) => {
    setPrefilling(true)
    try {
      await prefillObligations(bpId)
      startPolling(silent)
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.error || 'Could not start AI prefill.')
      setPrefilling(false)
    }
  }

  const updateRow = (id, patch) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch, _dirty: true } : r))
  }

  const toggleExpand = id => setExpanded(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const toggleLegCollapse = leg => setCollapsedLegs(prev => {
    const next = new Set(prev)
    next.has(leg) ? next.delete(leg) : next.add(leg)
    return next
  })

  const dirtyRows = useMemo(() => rows.filter(r => r._dirty), [rows])

  const allReviewed = useMemo(
    () => rows.length > 0 && rows.every(r => r.reviewedAt),
    [rows]
  )

  const prefillStatus = useMemo(() => {
    const classified = rows.filter(r => r.aiPrefilledAt).length
    const notApplicable = rows.filter(r => r.isSkipped && (r.skipReason || '').startsWith('AI:')).length
    const lastRun = rows.reduce((acc, r) => (r.aiPrefilledAt && (!acc || r.aiPrefilledAt > acc)) ? r.aiPrefilledAt : acc, null)
    return { classified, total: rows.length, notApplicable, lastRun }
  }, [rows])

  const legislationGroups = useMemo(() => {
    const acc = new Map()
    rows.forEach(r => {
      const key = r.canonicalLegislation || 'Unclassified'
      if (!acc.has(key)) {
        acc.set(key, {
          framework: key,
          citationUrls: new Set(),
          rows: [],
          appliesCount: 0,
          skippedCount: 0,
          aiHighConfidence: 0,
        })
      }
      const bucket = acc.get(key)
      bucket.rows.push(r)
      if (r.citationUrl) bucket.citationUrls.add(r.citationUrl)
      if (r.isSkipped) bucket.skippedCount++
      else bucket.appliesCount++
      if ((r.aiPrefillConfidence ?? 0) >= 0.7) bucket.aiHighConfidence++
    })
    return [...acc.values()]
      .map(g => ({ ...g, citationUrl: g.citationUrls.size === 1 ? [...g.citationUrls][0] : null }))
      .sort((a, b) => b.rows.length - a.rows.length)
  }, [rows])

  const visibleRows = useMemo(() => {
    return rows.filter(r => {
      if (filterDomain !== 'all' && r.taskDomainId !== filterDomain) return false
      if (filterApplies === 'applies' && r.isSkipped) return false
      if (filterApplies === 'skipped' && !r.isSkipped) return false
      if (filterText && !`${r.name} ${r.regulatoryFramework || ''}`.toLowerCase().includes(filterText.toLowerCase())) return false
      return true
    })
  }, [rows, filterDomain, filterApplies, filterText])

  const changedFields = (row, base) => {
    const prev = base || {}
    const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
    const payload = {}
    if (!same(row.isSkipped, prev.isSkipped)) payload.is_skipped = row.isSkipped
    if (!same(row.skipReason, prev.skipReason)) payload.skip_reason = row.skipReason ?? null
    if (!same(row.cadenceDays, prev.cadenceDays)) payload.cadence_days = row.cadenceDays ?? null
    if (!same(row.criticalityWeight, prev.criticalityWeight)) payload.criticality_weight = row.criticalityWeight
    if (!same(row.toleranceDays, prev.toleranceDays)) payload.tolerance_days = row.toleranceDays
    if (!same(row.acceptedTagIds || [], prev.acceptedTagIds || [])) payload.accepted_tag_ids = row.acceptedTagIds || []
    const scope = row.scope || 'building'
    const prevScope = prev.scope || 'building'
    if (scope !== prevScope) payload.scope = scope
    const unitIds = scope === 'unit' ? (row.appliesToUnitIds || []) : null
    const prevUnitIds = prevScope === 'unit' ? (prev.appliesToUnitIds || []) : null
    if (!same(unitIds, prevUnitIds)) payload.applies_to_unit_ids = unitIds
    if (!same(row.obligationKind || 'file_type', prev.obligationKind || 'file_type')) {
      payload.obligation_kind = row.obligationKind || 'file_type'
    }
    if (!same(row.fileTypeId ?? null, prev.fileTypeId ?? null)) payload.file_type_id = row.fileTypeId || null
    if (!same(row.taskDomainId ?? null, prev.taskDomainId ?? null)) payload.task_domain_id = row.taskDomainId || null
    return payload
  }

  const saveAll = async () => {
    if (!dirtyRows.length) return
    setSaving(true)
    try {
      const updates = dirtyRows
        .map(r => ({ id: r.id, payload: changedFields(r, original[r.id]) }))
        .filter(u => Object.keys(u.payload).length > 0)
      if (updates.length === 0) {
        setRows(prev => prev.map(r => ({ ...r, _dirty: false })))
        return
      }
      await bulkUpdateObligations(bpId, updates)
      try {
        await markProgrammeReviewed(bpId)
        setReviewConfirmed(true)
        toast.success(`Saved ${updates.length} change${updates.length === 1 ? '' : 's'}. All obligations marked as reviewed.`)
      } catch (reviewError) {
        toast.success(`Saved ${updates.length} change${updates.length === 1 ? '' : 's'}.`)
      }
      await load()
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.response?.data?.error || 'Save failed. Try again.')
    } finally {
      setSaving(false)
    }
  }

  const [reviewing, setReviewing] = useState(false)
  const [reviewConfirmed, setReviewConfirmed] = useState(false)
  const confirmReview = async () => {
    if (reviewing) return
    setReviewing(true)
    try {
      const res = await markProgrammeReviewed(bpId)
      const newlyReviewed = res?.data?.data?.newlyReviewed ?? 0
      setReviewConfirmed(true)
      toast.success(newlyReviewed > 0
        ? `Review confirmed: ${newlyReviewed} obligation${newlyReviewed === 1 ? '' : 's'} marked as reviewed.`
        : 'All obligations were already reviewed.')
      await load({ silent: true })
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not confirm the review.')
    } finally {
      setReviewing(false)
    }
  }

  const discardAll = () => {
    setRows(Object.values(original).map(r => ({ ...r, _dirty: false })))
  }

  const addObligationModal = addingObligation && (
    <AddProgrammeObligationModal
      bpId={bpId}
      onClose={() => setAddingObligation(false)}
      onAdded={() => load({ silent: true })}
    />
  )

  const customiseModal = customiseTarget && (
    <CustomiseObligationModal
      bpId={bpId}
      bpName={bpName}
      obligation={customiseTarget}
      availableTags={tags.flatMap(g => (g.tags || []).map(t => ({ ...t, layer: g.layer })))}
      availableDomains={domains}
      onClose={() => setCustomiseTarget(null)}
      onSaved={() => { setCustomiseTarget(null); load({ silent: true }) }}
    />
  )

  const unitsModal = unitsTarget && createPortal(
    <UnitsOverlay onClick={() => setUnitsTarget(null)}>
      <UnitsBox onClick={e => e.stopPropagation()}>
        <UnitsHead>
          <UnitsTitle>Units: {unitsTarget.name}</UnitsTitle>
          <UnitsClose type='button' onClick={() => setUnitsTarget(null)} aria-label='Close'>✕</UnitsClose>
        </UnitsHead>
        <UnitsBody>
          <UnitsHint>Choose which Unit Passports this obligation applies to. It only counts towards those units’ compliance.</UnitsHint>
          <UnitMultiSelect
            options={units}
            selectedIds={unitsTarget.appliesToUnitIds || []}
            onChange={ids => { updateRow(unitsTarget.id, { appliesToUnitIds: ids }); setUnitsTarget(t => (t ? { ...t, appliesToUnitIds: ids } : t)) }} />
        </UnitsBody>
        <UnitsFoot>
          <UnitsDone type='button' onClick={() => setUnitsTarget(null)}>Done</UnitsDone>
        </UnitsFoot>
      </UnitsBox>
    </UnitsOverlay>,
    document.body
  )

  if (loading) return <SkeletonRows rows={7} widths={['38%', '20%', '16%', '12%']} />
  if (rows.length === 0) return (
    <EmptyPanel>
      <h3>No obligations on this Building Passport yet</h3>
      <p>Run the obligation library setup to populate this table, or add one yourself.</p>
      <EmptyAddButton onClick={() => setAddingObligation(true)}>
        <AddRoundedIcon style={{ fontSize: 16 }} />
        Add obligation
      </EmptyAddButton>
      {addObligationModal}
      {customiseModal}
      {unitsModal}
    </EmptyPanel>
  )

  return (
    <Wrap>
      <Bar>
        <BarLeft>
          <SearchWrap>
            <SearchRoundedIcon style={{ fontSize: 16, color: '#7a7a83' }} />
            <SearchInput
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              placeholder='Filter by name or framework…' />
          </SearchWrap>
          <SearchableSelect
            value={filterDomain}
            onChange={v => setFilterDomain(v)}
            options={[{ value: 'all', label: 'All domains' }, ...domains.map(d => ({ value: d.id, label: d.name }))]}
            ariaLabel='Filter by domain'
          />
          <SearchableSelect
            value={filterApplies}
            onChange={v => setFilterApplies(v)}
            options={[
              { value: 'all', label: 'All status' },
              { value: 'applies', label: 'Applies' },
              { value: 'skipped', label: 'Not applicable', color: RAG.none.fg },
            ]}
            ariaLabel='Filter by status'
          />
          <Counter>
            {visibleRows.length} of {rows.length} obligations
          </Counter>
          <TaskVsEvidenceExplainer />
        </BarLeft>
        <BarRight>
          <GroupToggle role='tablist' aria-label='Group obligations'>
            <GroupToggleBtn $active={groupBy === 'flat'} onClick={() => setGroupBy('flat')}>
              <FormatListBulletedRoundedIcon style={{ fontSize: 13 }} />
              Flat
            </GroupToggleBtn>
            <GroupToggleBtn $active={groupBy === 'legislation'} onClick={() => setGroupBy('legislation')}>
              <AccountBalanceRoundedIcon style={{ fontSize: 13 }} />
              By legislation
            </GroupToggleBtn>
          </GroupToggle>
          <PrefillStatus title={prefillStatus.lastRun ? `Last run ${new Date(prefillStatus.lastRun).toLocaleString('en-GB')}` : undefined}>
            {prefilling
              ? 'AI applicability check running…'
              : prefillStatus.classified > 0
                ? `AI checked ${prefillStatus.classified}/${prefillStatus.total} · ${prefillStatus.notApplicable} marked not applicable`
                : 'AI applicability check not run yet'}
          </PrefillStatus>
          <RerunButton
            onClick={() => runPrefill()}
            disabled={prefilling}
            title="Asks Guru to re-assess which compliance requirements apply to this building, and how confident it is in each. Every result is only a suggestion you can review and change. No obligation is ever removed."
          >
            <AutoAwesomeRoundedIcon style={{ fontSize: 14 }} />
            {prefilling ? 'Checking…' : 'Re-run AI prefill'}
          </RerunButton>
          <AiActionExplainer
            label='About the AI check'
            title='How the AI applicability check works'
            lead={(
              <>
                An obligation is a compliance requirement this building has to meet. A recurring legal or contractual
                duty, such as keeping a valid EICR on file or carrying out an annual fire-door inspection. This check
                works out which of them actually apply to your building.
              </>
            )}
            rows={[
              ['What it does', 'It reads this building’s details and documents and decides which requirements from the standard library apply here. For example, only higher-risk-building duties if the building is higher-risk.'],
              ['It only suggests. You decide', 'Anything it marks “not applicable” is a suggestion, not a decision. Nothing is removed; you can switch any requirement back on or off yourself.'],
              ['What the status means', '“AI checked 8/10 · 2 marked not applicable” means it assessed 10 requirements and judged 2 don’t apply to this building. The rest stay on your tracker.'],
              ['Re-running it', 'Run it again after you upload more documents or change the building’s details, and it re-assesses with the new information.'],
            ]}
            tip={(
              <>
                <strong>Missing something?</strong> Use <em>Add obligation</em> to track any requirement the check hasn’t
                picked up.
              </>
            )}
          />
          <AddButton onClick={() => setAddingObligation(true)}>
            <AddRoundedIcon style={{ fontSize: 16 }} />
            Add obligation
          </AddButton>
        </BarRight>
      </Bar>

      {(() => {
        const renderRow = (r) => {
          const isFileKind = (r.obligationKind || 'file_type') === 'file_type'
          const cadenceHint = r.cadenceDays
            ? `Each ${isFileKind ? 'upload' : 'completion'} satisfies for ${r.cadenceDays} days`
            : isFileKind
              ? 'One-off. Uploaded evidence never expires'
              : 'One-off. Completion never expires'
          const isExpanded = expanded.has(r.id)
          return (
            <React.Fragment key={r.id}>
              <Row $dirty={r._dirty} $skipped={r.isSkipped}>
                <ColObligation>
                  <NameRow>
                    <ExpandBtn
                      onClick={() => toggleExpand(r.id)}
                      $open={isExpanded}
                      aria-label={isExpanded ? 'Hide details' : 'Show description, penalty and legislation'}
                      title={isExpanded ? 'Hide details' : 'Show description, penalty and legislation'}>
                      <ChevronRightRoundedIcon style={{ fontSize: 18 }} />
                    </ExpandBtn>
                    <RowName>{r.name}</RowName>
                  </NameRow>
                  <RowMeta>
                    {(() => {
                      const crit = CRITICALITY_LEVELS.find(c => c.value === r.criticalityWeight) || CRITICALITY_LEVELS[0]
                      return <CritChip $tint={crit.colour} title={`Criticality: ${crit.label}. Change it under Customise`}>{crit.label}</CritChip>
                    })()}
                    {r.regulatoryFramework && (groupBy !== 'legislation' || r.regulatoryFramework !== r.canonicalLegislation) && <RowChip>{r.regulatoryFramework}</RowChip>}
                    {r.penaltySeverity && (
                      <RowChip $tone='warn'>
                        <WarningAmberRoundedIcon style={{ fontSize: 12, marginRight: 3 }} />
                        {humanisePenalty(r.penaltySeverity)}
                      </RowChip>
                    )}
                    {r.taskDomainName && (
                      <RowChip $tint={r.taskDomainColour}>{r.taskDomainName}</RowChip>
                    )}
                  </RowMeta>
                </ColObligation>
                <ColApplies>
                  <Toggle
                    $on={!r.isSkipped}
                    onClick={() => updateRow(r.id, { isSkipped: !r.isSkipped })}
                    title={r.isSkipped ? 'Mark as applicable' : 'Mark as not applicable'}>
                    <ToggleKnob $on={!r.isSkipped} />
                  </Toggle>
                </ColApplies>
                <ColScope>
                  <PillRow>
                    {SCOPE_OPTIONS.map(opt => (
                      <ScopePill
                        key={opt.value}
                        $active={(r.scope || 'building') === opt.value}
                        onClick={() => updateRow(r.id, {
                          scope: opt.value,
                          appliesToUnitIds: opt.value === 'unit' ? (r.appliesToUnitIds || []) : [],
                        })}>
                        {opt.label}
                      </ScopePill>
                    ))}
                  </PillRow>
                  {(r.scope || 'building') === 'unit' && (
                    <UnitsBtn type='button' onClick={() => setUnitsTarget(r)}>
                      {(r.appliesToUnitIds || []).length > 0
                        ? `${r.appliesToUnitIds.length} unit${r.appliesToUnitIds.length === 1 ? '' : 's'} selected`
                        : 'Choose units'}
                    </UnitsBtn>
                  )}
                </ColScope>
                <ColSatisfied>
                  <KindToggle role='group' aria-label='How this obligation is met'>
                    <KindSeg $active={isFileKind} type='button'
                      title='File. Met by uploading a document of the matching type (a certificate or report).'
                      onClick={() => updateRow(r.id, { obligationKind: 'file_type' })}>
                      <DescriptionRoundedIcon style={{ fontSize: 14 }} /> File
                    </KindSeg>
                    <KindSeg $active={!isFileKind} type='button'
                      title='Task. Met by completing a recurring task in the matching domain that gets signed off.'
                      onClick={() => updateRow(r.id, { obligationKind: 'task' })}>
                      <TaskAltRoundedIcon style={{ fontSize: 14 }} /> Task
                    </KindSeg>
                  </KindToggle>
                  {isFileKind ? (
                    <RefPicker
                      label='file type'
                      options={fileTypes}
                      selectedId={r.fileTypeId}
                      selectedName={r.fileTypeName}
                      onChange={(id, name) => updateRow(r.id, { fileTypeId: id, fileTypeName: name })} />
                  ) : (
                    <RefPicker
                      label='domain'
                      options={domains}
                      selectedId={r.taskDomainId}
                      selectedName={r.taskDomainName}
                      onChange={(id, name) => updateRow(r.id, { taskDomainId: id, taskDomainName: name })} />
                  )}
                  {(() => {
                    const fmt = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : null
                    if (r.openTask) {
                      return (
                        <TaskLink type='button'
                          title='Open the current task set up for this obligation'
                          onClick={(e) => { e.stopPropagation(); redirect(PRIVATE_PATHS.taskDetails.replace(':id', r.openTask.id)) }}>
                          <TaskAltRoundedIcon style={{ fontSize: 13, flexShrink: 0 }} />
                          <TaskLinkText>{r.openTask.title}</TaskLinkText>
                          {r.openTask.deadline && <TaskDue>· due {fmt(r.openTask.deadline)}</TaskDue>}
                        </TaskLink>
                      )
                    }
                    if (r.status === 'satisfied') {
                      const nd = fmt(r.nextDueAt)
                      return (
                        <SatChip $tint='#15803d'
                          title={`Satisfied by current evidence or a completed task.${r.lastSatisfiedAt ? ` Last satisfied ${fmt(r.lastSatisfiedAt)}.` : ''}${nd ? ` Next due ${nd}.` : ''}`}>
                          ✓ Satisfied{nd ? ` · next due ${nd}` : ''}
                        </SatChip>
                      )
                    }
                    if (r.status === 'in_review') return <SatChip $tint='#d97706'>Evidence in review</SatChip>
                    if (r.status === 'expired') return <SatChip $tint='#b00020'>Expired{r.nextDueAt ? ` · was due ${fmt(r.nextDueAt)}` : ''}</SatChip>
                    if (r.status === 'overdue') return <SatChip $tint='#b00020'>Overdue{r.nextDueAt ? ` · due ${fmt(r.nextDueAt)}` : ''}</SatChip>
                    if (!isFileKind && r.nextDueAt) return <SatChip $tint='#0e7490'>Next task due {fmt(r.nextDueAt)}</SatChip>
                    return null
                  })()}
                </ColSatisfied>
                <ColCadence>
                  <CadenceSelect
                    value={r.cadenceDays}
                    onChange={days => updateRow(r.id, { cadenceDays: days })} />
                  <CadenceHint>{cadenceHint}</CadenceHint>
                </ColCadence>
                <ColActions>
                  <CustomiseBtn
                    type='button'
                    onClick={() => setCustomiseTarget(r)}
                    title='Customise this obligation. Rename, cadence, criticality, tolerance, domain and tags'>
                    <TuneRoundedIcon style={{ fontSize: 15 }} />
                    <span>Customise</span>
                  </CustomiseBtn>
                </ColActions>
              </Row>
              {isExpanded && (
                <ExpandedRow>
                  <ExpandedInner>
                    {r.description && (
                      <ExpandedBlock>
                        <ExpandedLabel>What this is</ExpandedLabel>
                        <ExpandedText>{r.description}</ExpandedText>
                      </ExpandedBlock>
                    )}
                    {r.aiPrefillReasoning && (
                      <ExpandedBlock>
                        <ExpandedLabel>
                          AI assessment{r.aiPrefillConfidence != null ? ` · ${Math.round(r.aiPrefillConfidence * 100)}% confident` : ''}
                        </ExpandedLabel>
                        <ExpandedText>{r.aiPrefillReasoning}</ExpandedText>
                      </ExpandedBlock>
                    )}
                    <ExpandedBlock>
                      <ExpandedLabel>Reference</ExpandedLabel>
                      <ExpandedRefs>
                        {r.regulatoryFramework && <ExpandedRefChip>{r.regulatoryFramework}</ExpandedRefChip>}
                        {r.penaltySeverity && (
                          <ExpandedRefChip $tone='warn'>
                            Penalty: {humanisePenalty(r.penaltySeverity)}
                          </ExpandedRefChip>
                        )}
                        {r.citationUrl && (
                          <ExpandedLink href={r.citationUrl} target='_blank' rel='noopener noreferrer'>
                            <GavelRoundedIcon style={{ fontSize: 14 }} />
                            Read legislation
                            <OpenInNewRoundedIcon style={{ fontSize: 13 }} />
                          </ExpandedLink>
                        )}
                      </ExpandedRefs>
                    </ExpandedBlock>
                  </ExpandedInner>
                </ExpandedRow>
              )}
            </React.Fragment>
          )
        }
        const orderedRows = groupBy === 'legislation'
          ? legislationGroups.flatMap(g => g.rows.filter(r => visibleRows.includes(r)))
          : visibleRows
        let lastFramework = null
        return (
          <TableWrap>
            <TableHead>
              <ColObligation>Obligation</ColObligation>
              <ColApplies>Applies</ColApplies>
              <ColScope>Scope</ColScope>
              <ColSatisfied>
                <HeaderLabelRow>
                  Satisfied by
                  <HeaderInfo title='Choose how each obligation is evidenced. File = met by uploading a matching document (a certificate or report). Task = met by completing a recurring task or inspection that gets signed off.'>ⓘ</HeaderInfo>
                </HeaderLabelRow>
              </ColSatisfied>
              <ColCadence>Cadence</ColCadence>
              <ColActions>Customise</ColActions>
            </TableHead>
            {orderedRows.map(r => {
              const framework = r.canonicalLegislation || r.regulatoryFramework
              const showHeader = groupBy === 'legislation' && framework !== lastFramework
              lastFramework = framework
              const group = showHeader ? legislationGroups.find(g => g.framework === framework) : null
              const isCollapsed = collapsedLegs.has(framework)
              return (
                <React.Fragment key={`wrapper-${r.id}`}>
                  {showHeader && (
                    <LegislationHeaderRow onClick={() => toggleLegCollapse(framework)}>
                      <LegChevron $open={!isCollapsed}>
                        <ChevronRightRoundedIcon style={{ fontSize: 18 }} />
                      </LegChevron>
                      <LegName>{framework}</LegName>
                      <LegMeta>
                        <LegCount>{group?.appliesCount ?? 0} applies</LegCount>
                        {(group?.skippedCount ?? 0) > 0 && (
                          <LegCount $muted>{group.skippedCount} not applicable</LegCount>
                        )}
                        {(group?.aiHighConfidence ?? 0) > 0 && (
                          <LegCount $accent>{group.aiHighConfidence} AI-confident</LegCount>
                        )}
                      </LegMeta>
                      {group?.citationUrl && (
                        <LegCitation
                          href={group.citationUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          onClick={e => e.stopPropagation()}>
                          <GavelRoundedIcon style={{ fontSize: 14 }} />
                          Read
                          <OpenInNewRoundedIcon style={{ fontSize: 12 }} />
                        </LegCitation>
                      )}
                    </LegislationHeaderRow>
                  )}
                  {(groupBy !== 'legislation' || !isCollapsed) && renderRow(r)}
                </React.Fragment>
              )
            })}
          </TableWrap>
        )
      })()}

      {dirtyRows.length > 0 && (
        <SaveBar>
          <SaveBarText>
            <strong>{dirtyRows.length}</strong> unsaved change{dirtyRows.length === 1 ? '' : 's'}. Saving updates how these obligations are configured
          </SaveBarText>
          <SaveBarActions>
            <DiscardButton onClick={discardAll} disabled={saving}>Discard</DiscardButton>
            <SaveButton onClick={saveAll} disabled={saving}>
              {saving ? 'Saving…' : `Save ${dirtyRows.length} change${dirtyRows.length === 1 ? '' : 's'}`}
              {!saving && <CheckRoundedIcon style={{ fontSize: 16 }} />}
            </SaveButton>
          </SaveBarActions>
        </SaveBar>
      )}

      {dirtyRows.length === 0 && rows.length > 0 && !allReviewed && !reviewConfirmed && (
        <SaveBar $inline>
          <SaveBarText>
            Reviewed how these obligations are configured? Confirming unlocks this building's compliance score.
          </SaveBarText>
          <SaveBarActions>
            <SaveButton onClick={confirmReview} disabled={reviewing}>
              {reviewing ? 'Confirming…' : 'Mark all as reviewed'}
              {!reviewing && <CheckRoundedIcon style={{ fontSize: 16 }} />}
            </SaveButton>
          </SaveBarActions>
        </SaveBar>
      )}

      {addObligationModal}
      {customiseModal}
      {unitsModal}
    </Wrap>
  )
}

export default ObligationSetupTable

const confidenceTint = v => v >= 0.8 ? '#2aa6b3' : v >= 0.5 ? '#33becc' : v >= 0.3 ? '#d97706' : '#b00020'

const humanisePenalty = v => {
  if (!v) return ''
  const map = {
    imprisonment: 'Imprisonment',
    unlimited_fine: 'Unlimited fine',
    fine: 'Fine',
    enforcement_notice: 'Enforcement notice',
    none: 'No statutory penalty',
  }
  return map[v] || v.replace(/_/g, ' ')
}

const CadenceSelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false)
  const label = cadenceLabel(value)
  return (
    <Popover>
      <PopoverButton onClick={() => setOpen(o => !o)}>
        {label}
      </PopoverButton>
      {open && (
        <PopoverPanel onMouseLeave={() => setOpen(false)}>
          {CADENCE_PRESETS.map(p => (
            <PopoverItem key={p.label}
              $active={value === p.days}
              onClick={() => { onChange(p.days); setOpen(false) }}>
              {p.label}{p.days !== null ? ` · ${p.days}d` : ''}
            </PopoverItem>
          ))}
          <CadenceCustomRow onClick={e => e.stopPropagation()}>
            <CadenceCustomInput
              type='number'
              min={1}
              max={7300}
              placeholder='Custom'
              value={CADENCE_PRESETS.some(p => p.days === value) ? '' : (value ?? '')}
              onChange={e => onChange(e.target.value ? Number(e.target.value) : null)} />
            <CadenceCustomUnit>days</CadenceCustomUnit>
          </CadenceCustomRow>
        </PopoverPanel>
      )}
    </Popover>
  )
}

const CriticalitySelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false)
  const cur = CRITICALITY_LEVELS.find(c => c.value === value) || CRITICALITY_LEVELS[0]
  return (
    <Popover>
      <CritPill $tint={cur.colour} onClick={() => setOpen(o => !o)}>
        {cur.label}
      </CritPill>
      {open && (
        <PopoverPanel onMouseLeave={() => setOpen(false)}>
          {CRITICALITY_LEVELS.map(c => (
            <PopoverItem key={c.value}
              $active={value === c.value}
              onClick={() => { onChange(c.value); setOpen(false) }}>
              <span style={{ width: 10, height: 10, background: c.colour, borderRadius: '50%', marginRight: 8, display: 'inline-block' }} />
              {c.label}
            </PopoverItem>
          ))}
        </PopoverPanel>
      )}
    </Popover>
  )
}

const TagPicker = ({ tags, selectedIds, onChange }) => {
  const [open, setOpen] = useState(false)
  const toggle = id => {
    const set = new Set(selectedIds)
    if (set.has(id)) set.delete(id); else set.add(id)
    onChange([...set])
  }
  const selectedNames = tags.flatMap(g => g.tags || []).filter(t => selectedIds.includes(t.id))
  return (
    <Popover>
      <PopoverButton onClick={() => setOpen(o => !o)}>
        {selectedNames.length === 0
          ? 'Any matching file'
          : `${selectedNames.length} tag${selectedNames.length === 1 ? '' : 's'}`}
      </PopoverButton>
      {open && (
        <TagPanel onMouseLeave={() => setOpen(false)}>
          {tags.length === 0 ? (
            <EmptyHint>No tags configured on this Building Passport yet.</EmptyHint>
          ) : tags.map(group => (
            <TagGroup key={group.layer}>
              <TagLayerHead>{group.layer}</TagLayerHead>
              <TagGrid>
                {(group.tags || []).map(t => (
                  <TagChip key={t.id}
                    $active={selectedIds.includes(t.id)}
                    onClick={() => toggle(t.id)}>
                    {t.name}
                  </TagChip>
                ))}
              </TagGrid>
            </TagGroup>
          ))}
        </TagPanel>
      )}
    </Popover>
  )
}

const RefPicker = ({ label, options, selectedId, selectedName, onChange }) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const display = selectedName || `Pick ${label}…`
  const q = query.trim().toLowerCase()
  const filtered = q ? options.filter(o => (o.name || '').toLowerCase().includes(q)) : options
  return (
    <Popover>
      <RefPickerBtn onClick={() => setOpen(o => !o)} $empty={!selectedId}>
        {display}
      </RefPickerBtn>
      {open && (
        <PopoverPanel onMouseLeave={() => { setOpen(false); setQuery('') }} style={{ minWidth: 210, maxHeight: 280, overflowY: 'auto' }}>
          <RefSearch
            autoFocus
            placeholder={`Search ${label}…`}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onClick={e => e.stopPropagation()}
          />
          {filtered.length === 0
            ? <EmptyHint>{options.length === 0 ? 'No options available.' : 'No matches.'}</EmptyHint>
            : filtered.map(o => (
              <PopoverItem key={o.id}
                $active={selectedId === o.id}
                onClick={() => { onChange(o.id, o.name); setOpen(false); setQuery('') }}>
                {o.name}
              </PopoverItem>
            ))}
        </PopoverPanel>
      )}
    </Popover>
  )
}

const RefSearch = styled.input`
  position: sticky;
  top: 0;
  width: 100%;
  box-sizing: border-box;
  font: inherit;
  font-size: 12.5px;
  border: none;
  border-bottom: 1px solid #ececec;
  padding: 8px 10px;
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti};
  &:focus { outline: none; }
`

const CoverageChip = styled.span`
  display: inline-flex; align-items: center;
  margin-top: 6px;
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  color: ${({ $tint }) => $tint};
  background: ${({ $tint }) => `${$tint}14`};
  width: fit-content;
`

const SourceLine = styled.span`
  display: block;
  margin-top: 3px;
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const CadenceCustomRow = styled.div`
  display: flex; align-items: center; gap: 6px;
  padding: 6px 10px;
  border-top: 1px solid #f4f4f6;
`

const CadenceCustomInput = styled.input`
  width: 70px;
  font: inherit; font-size: 12.5px;
  padding: 4px 8px;
  border: 1px solid #ececec; border-radius: 6px;
  &:focus { outline: 2px solid #33becc; outline-offset: -1px; }
`

const CadenceCustomUnit = styled.span`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const UnitMultiSelect = ({ options, selectedIds, onChange }) => {
  const [open, setOpen] = useState(false)
  const toggle = id => {
    const set = new Set(selectedIds)
    if (set.has(id)) set.delete(id); else set.add(id)
    onChange([...set])
  }
  const summary = selectedIds.length === 0
    ? 'Select Unit Passports…'
    : selectedIds.length === options.length
      ? 'All Unit Passports'
      : `${selectedIds.length} of ${options.length}`
  return (
    <Popover>
      <PopoverButton onClick={() => setOpen(o => !o)}>
        {summary}
      </PopoverButton>
      {open && (
        <UnitPanel onMouseLeave={() => setOpen(false)}>
          <PanelActions>
            <PanelAction onClick={() => onChange(options.map(o => o.id))}>Select all</PanelAction>
            <PanelAction onClick={() => onChange([])}>Clear</PanelAction>
          </PanelActions>
          <UnitList>
            {options.length === 0 && <EmptyHint>This Building Passport has no Unit Passports yet.</EmptyHint>}
            {options.map(u => (
              <UnitRow key={u.id} onClick={() => toggle(u.id)}>
                <UnitCheckbox $on={selectedIds.includes(u.id)}>
                  {selectedIds.includes(u.id) && <CheckRoundedIcon style={{ fontSize: 12, color: '#fff' }} />}
                </UnitCheckbox>
                <UnitLabel>
                  <strong>{u.plotNumber || u.name || '—'}</strong>
                  {u.floorNumbers && u.floorNumbers.length > 0 && (
                    <UnitFloor>Floor {u.floorNumbers.join(', ')}</UnitFloor>
                  )}
                </UnitLabel>
              </UnitRow>
            ))}
          </UnitList>
        </UnitPanel>
      )}
    </Popover>
  )
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`

const Wrap = styled.div` width: 100%; animation: ${fadeIn} 280ms ease both; `

const LoadingPanel = styled.div`
  padding: 28px;
  text-align: center;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  color: ${({ theme }) => theme.colors.grey[800]};
`
const EmptyPanel = styled.div`
  padding: 30px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  text-align: center;
  h3 { margin: 0 0 6px; font-family: 'FuturaPT-Light', system-ui, sans-serif; font-size: 18px; }
  p  { margin: 0; font-size: 13px; color: ${({ theme }) => theme.colors.grey[800]}; }
`
const EmptyAddButton = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  margin-top: 16px;
  padding: 8px 16px;
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
  color: #fff;
  cursor: pointer;
  transition: filter 160ms ease;
  &:hover { filter: brightness(1.06); }
`

const Bar = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 12px 12px 0 0;
  flex-wrap: wrap;
`
const BarLeft = styled.div` display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap; `
const BarRight = styled.div` margin-left: auto; display: inline-flex; gap: 8px; align-items: center; `

const GroupToggle = styled.div`
  display: inline-flex;
  padding: 3px;
  background: #f4f4f6;
  border-radius: 999px;
  border: 1px solid #ececec;
`
const GroupToggleBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px;
  background: ${({ $active }) => $active ? '#fff' : 'transparent'};
  color: ${({ $active, theme }) => $active ? theme.colors.haiti : theme.colors.grey[800]};
  border: 1px solid ${({ $active }) => $active ? '#e0e0e4' : 'transparent'};
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: all 160ms ease;
  box-shadow: ${({ $active }) => $active ? '0 1px 3px rgba(12, 18, 41, 0.06)' : 'none'};
`

const LegislationHeaderRow = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 10px 16px;
  background: linear-gradient(to right, #fafbfc 0%, #fff 80%);
  border-top: 1px solid #ececec;
  border-bottom: 1px solid #ececec;
  cursor: pointer;
  &:first-of-type { border-top: none; }
  &:hover { background: #f4f4f6; }
`
const LegChevron = styled.span`
  display: inline-flex; align-items: center; justify-content: center;
  color: ${({ theme }) => theme.colors.grey[800]};
  & > svg {
    transform: rotate(${({ $open }) => $open ? '90deg' : '0deg'});
    transition: transform 180ms ease;
  }
`
const LegName = styled.span`
  font-size: 13px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
  letter-spacing: 0.02em;
`
const LegMeta = styled.span`
  display: inline-flex; gap: 8px; align-items: center;
  margin-left: auto;
`
const LegCount = styled.span`
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.04em;
  padding: 2px 9px;
  border-radius: 999px;
  background: ${({ $muted, $accent }) =>
    $muted ? '#f4f4f6'
    : $accent ? 'rgba(51,190,204,0.10)'
    : 'rgba(42,166,179,0.10)'};
  color: ${({ $muted, $accent, theme }) =>
    $muted ? theme.colors.grey[800]
    : $accent ? '#2aa6b3'
    : '#2aa6b3'};
  border: 1px solid ${({ $muted, $accent }) =>
    $muted ? '#ececec'
    : $accent ? 'rgba(51,190,204,0.32)'
    : 'rgba(42,166,179,0.28)'};
`
const LegCitation = styled.a`
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.secondary};
  text-decoration: none;
  padding: 3px 9px;
  border-radius: 999px;
  background: rgba(51,190,204,0.08);
  border: 1px solid rgba(51,190,204,0.22);
  &:hover { background: rgba(51,190,204,0.16); }
`

const SearchWrap = styled.div`
  display: inline-flex; align-items: center; gap: 6px;
  background: #fafbfc;
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 6px 10px;
  min-width: 220px;
`
const SearchInput = styled.input`
  font: inherit;
  border: none;
  background: transparent;
  outline: none;
  flex: 1;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  &::placeholder { color: ${({ theme }) => theme.colors.grey[800]}; }
`
const Counter = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.grey[800]};
  margin-left: 4px;
`
const RerunButton = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  transition: all 160ms ease;
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.haiti}; color: #fff;
    border-color: ${({ theme }) => theme.colors.haiti};
  }
  &:disabled { opacity: 0.6; cursor: wait; }
`
const AddButton = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 14px;
  background: ${({ theme }) => theme.colors.secondary};
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  color: #fff;
  cursor: pointer;
  transition: filter 160ms ease;
  &:hover { filter: brightness(1.06); }
`

const PrefillStatus = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey[800]};
  white-space: nowrap;
`

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  background: #fff;
  border: 1px solid #ececec;
  border-top: none;
  border-radius: 0 0 14px 14px;
`

const TableHead = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 2.6fr) 56px minmax(140px, 1.4fr) minmax(160px, 2fr) minmax(130px, 1.3fr) 120px;
  gap: 10px;
  padding: 10px 16px;
  background: #fafbfc;
  border-bottom: 1px solid #ececec;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 2.6fr) 56px minmax(140px, 1.4fr) minmax(160px, 2fr) minmax(130px, 1.3fr) 120px;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: 1px solid #f4f4f6;
  align-items: center;
  background: ${({ $dirty }) => ($dirty ? '#f1fdfe' : '#fff')};
  ${({ $skipped }) => $skipped ? 'opacity: 0.55;' : ''}
  transition: background 180ms ease, opacity 180ms ease;
  &:hover { background: ${({ $dirty }) => $dirty ? '#e9fbfc' : '#fafbfc'}; }
`

const ColObligation = styled.div` display: flex; flex-direction: column; gap: 4px; min-width: 0; `
const ColApplies    = styled.div``
const ColScope      = styled.div``
const ColUnits      = styled.div``
const ColSatisfied  = styled.div` display: flex; flex-direction: column; gap: 4px; `

const HeaderLabelRow = styled.span`
  display: inline-flex;
  align-items: center;
`

const HeaderInfo = styled.span`
  margin-left: 5px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.grey[800]};
  cursor: help;
`
const ColCadence    = styled.div` display: flex; flex-direction: column; gap: 4px; `
const ColCrit       = styled.div``
const ColTags       = styled.div``
const ColAi         = styled.div` text-align: right; `
const ColActions    = styled.div` display: flex; justify-content: flex-end; align-items: center; min-width: 0; `

const NameRow = styled.div` display: inline-flex; align-items: center; gap: 6px; min-width: 0; `

const CustomiseBtn = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 24px;
  padding: 0 9px;
  border: 1px solid #d1d5db;
  border-radius: 7px;
  background: #fff;
  color: #374151;
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  svg { color: #6b7280; }
  &:hover { background: #f3f4f6; border-color: #9ca3af; }
`
const ExpandBtn = styled.button`
  font: inherit;
  width: 20px; height: 20px;
  border-radius: 6px;
  border: 1px solid #ececec;
  background: #fafbfc;
  color: ${({ theme }) => theme.colors.grey[800]};
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: background 180ms ease, color 180ms ease;
  & > svg {
    transform: rotate(${({ $open }) => $open ? '90deg' : '0deg'});
    transition: transform 180ms ease;
  }
  &:hover { background: #f0f0f4; color: ${({ theme }) => theme.colors.haiti}; }
`

const KindPill = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 10px;
  background: ${({ $active }) => $active ? '#33becc' : 'transparent'};
  color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.grey[800]};
  border: 1px solid ${({ $active }) => $active ? '#33becc' : 'transparent'};
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.04em;
  cursor: pointer;
  white-space: nowrap;
  transition: all 160ms ease;
  box-shadow: ${({ $active }) => $active ? '0 1px 3px rgba(12, 18, 41, 0.10)' : 'none'};
  &:hover { color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.haiti}; }
`

const KindToggle = styled.div`
  align-self: flex-start;
  display: inline-flex;
  padding: 2px;
  border-radius: 999px;
  background: #eef1f4;
  border: 1px solid #e1e6ea;
`
const KindSeg = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 12px;
  border: none;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  color: ${({ $active }) => ($active ? '#fff' : '#5b6472')};
  background: ${({ $active }) => ($active ? '#33becc' : 'transparent')};
  box-shadow: ${({ $active }) => ($active ? '0 1px 2px rgba(12, 18, 41, 0.14)' : 'none')};
  transition: background 140ms ease, color 140ms ease;
  &:hover { color: ${({ $active }) => ($active ? '#fff' : '#0C1229')}; }
`
const TaskLink = styled.button`
  font: inherit;
  align-self: flex-start;
  max-width: 100%;
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 9px;
  border-radius: 7px;
  border: 1px solid #cdeef1;
  background: #f0fbfc;
  color: #0e7490;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
  transition: background 140ms ease, border-color 140ms ease;
  &:hover { background: #e2f6f8; border-color: #7fd4dc; }
`
const TaskLinkText = styled.span` overflow: hidden; text-overflow: ellipsis; white-space: nowrap; `
const TaskDue = styled.span` flex-shrink: 0; font-weight: 600; opacity: 0.85; `
const SatChip = styled.span`
  align-self: flex-start;
  display: inline-flex; align-items: center;
  font-size: 11px; font-weight: 700;
  padding: 2px 9px; border-radius: 999px;
  color: ${({ $tint }) => $tint};
  background: ${({ $tint }) => `${$tint}14`};
`

const SatisfiedHint = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.grey[800]};
  letter-spacing: 0.02em;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`

const RefPickerBtn = styled.button`
  font: inherit;
  text-align: left;
  padding: 3px 8px;
  background: ${({ $empty }) => $empty ? '#fff4f2' : '#fafbfc'};
  border: 1px solid ${({ $empty }) => $empty ? '#b0002050' : '#e2e6ea'};
  border-radius: 7px;
  font-size: 11px;
  font-weight: 700;
  color: ${({ $empty, theme }) => $empty ? '#b00020' : theme.colors.haiti};
  cursor: pointer;
  align-self: flex-start;
  max-width: 100%;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  &:hover { background: ${({ $empty }) => $empty ? '#ffece8' : '#f0f0f4'}; border-color: #cbd5e1; }
`

const CadenceHint = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.grey[800]};
  letter-spacing: 0.02em;
  line-height: 1.3;
`

const ExpandedRow = styled.div`
  background: #fafbfc;
  border-bottom: 1px solid #f4f4f6;
  padding: 14px 16px 16px 40px;
`
const ExpandedInner = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: 18px;
  @media (max-width: 760px) { grid-template-columns: 1fr; }
`
const ExpandedBlock = styled.div` display: flex; flex-direction: column; gap: 6px; `
const ExpandedLabel = styled.span`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`
const ExpandedText = styled.p`
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.haiti};
`
const ExpandedRefs = styled.div` display: inline-flex; flex-wrap: wrap; gap: 6px; align-items: center; `
const ExpandedRefChip = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
  background: ${({ $tone }) => $tone === 'warn' ? '#b0002015' : '#f4f4f6'};
  color: ${({ $tone, theme }) => $tone === 'warn' ? '#b00020' : theme.colors.haiti};
  border: 1px solid ${({ $tone }) => $tone === 'warn' ? '#b0002035' : '#ececec'};
`
const ExpandedLink = styled.a`
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.secondary};
  text-decoration: none;
  &:hover { text-decoration: underline; }
`

const RowName = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`
const RowMeta = styled.div` display: inline-flex; gap: 4px; flex-wrap: wrap; align-items: center; `

const CritChip = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 999px;
  color: ${({ $tint }) => $tint};
  background: ${({ $tint }) => `${$tint}1a`};
  border: 1px solid ${({ $tint }) => `${$tint}40`};
`

const UnitsBtn = styled.button`
  margin-top: 6px;
  font: inherit;
  cursor: pointer;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 7px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #0e7490;
  font-size: 11.5px;
  font-weight: 700;
  &:hover { background: #f0fbfc; border-color: #67c9d3; }
`

const UnitsOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100010;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`
const UnitsBox = styled.div`
  width: 100%;
  max-width: 440px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.28);
`
const UnitsHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid #ececec;
`
const UnitsTitle = styled.h3` margin: 0; font-size: 15px; font-weight: 800; color: #0C1229; `
const UnitsClose = styled.button`
  font: inherit;
  cursor: pointer;
  border: none;
  background: none;
  font-size: 16px;
  color: #94a3b8;
  line-height: 1;
  &:hover { color: #475569; }
`
const UnitsBody = styled.div` padding: 16px 18px; overflow-y: auto; display: flex; flex-direction: column; gap: 12px; `
const UnitsHint = styled.p` margin: 0; font-size: 12.5px; line-height: 1.5; color: #64748b; `
const UnitsFoot = styled.div` padding: 12px 18px; border-top: 1px solid #ececec; display: flex; justify-content: flex-end; `
const UnitsDone = styled.button`
  font: inherit;
  cursor: pointer;
  border: none;
  border-radius: 9px;
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff;
  padding: 9px 18px;
  font-size: 13.5px;
  font-weight: 700;
  &:hover { filter: brightness(1.06); }
`
const RowChip = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 2px 7px;
  border-radius: 999px;
  background: ${({ $tint, $tone }) =>
    $tone === 'warn' ? '#b0002015'
    : $tone === 'ghost' ? '#f4f4f6'
    : $tint ? `${$tint}1A`
    : '#f4f4f6'};
  color: ${({ $tint, $tone, theme }) =>
    $tone === 'warn' ? '#b00020'
    : $tint || theme.colors.grey[800]};
  border: 1px solid ${({ $tint, $tone }) =>
    $tone === 'warn' ? '#b0002035'
    : $tint ? `${$tint}33`
    : '#ececec'};
`

const Toggle = styled.button`
  font: inherit;
  width: 36px; height: 20px;
  border-radius: 999px;
  background: ${({ $on }) => $on ? '#33becc' : '#cbcbd2'};
  border: none;
  position: relative;
  cursor: pointer;
  transition: background 180ms ease;
`
const ToggleKnob = styled.span`
  position: absolute;
  top: 2px; left: ${({ $on }) => $on ? '18px' : '2px'};
  width: 16px; height: 16px;
  background: #fff;
  border-radius: 50%;
  transition: left 180ms ease;
  box-shadow: 0 1px 3px rgba(12,18,41,0.3);
`

const PillRow = styled.div`
  display: inline-flex;
  gap: 2px;
  padding: 3px;
  background: #f4f4f6;
  border: 1px solid #ececec;
  border-radius: 999px;
`
const ScopePill = styled.button`
  font: inherit;
  padding: 3px 10px;
  background: ${({ $active }) => $active ? '#33becc' : 'transparent'};
  color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.grey[800]};
  border: 1px solid ${({ $active }) => $active ? '#33becc' : 'transparent'};
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: all 160ms ease;
  box-shadow: ${({ $active }) => $active ? '0 1px 3px rgba(12, 18, 41, 0.10)' : 'none'};
  &:hover { color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.haiti}; }
`

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: 12px;
`

const Popover = styled.div` position: relative; display: inline-block; `
const PopoverButton = styled.button`
  font: inherit;
  padding: 5px 10px;
  background: #fafbfc;
  border: 1px solid #ececec;
  border-radius: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  &:hover { background: #f0f0f4; }
`
const PopoverPanel = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 160px;
  z-index: 20;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 10px;
  box-shadow: 0 14px 32px rgba(12,18,41,0.12);
  padding: 6px;
  display: flex; flex-direction: column; gap: 2px;
`
const PopoverItem = styled.button`
  font: inherit;
  text-align: left;
  padding: 6px 10px;
  background: ${({ $active }) => $active ? 'rgba(51,190,204,0.10)' : 'transparent'};
  border: none;
  border-radius: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer;
  display: inline-flex; align-items: center;
  &:hover { background: rgba(51,190,204,0.16); }
`
const CritPill = styled.button`
  font: inherit;
  padding: 4px 10px;
  background: ${({ $tint }) => `${$tint}1A`};
  border: 1px solid ${({ $tint }) => `${$tint}40`};
  color: ${({ $tint }) => $tint};
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
`

const TagPanel = styled(PopoverPanel)` min-width: 260px; max-height: 320px; overflow-y: auto; padding: 10px; `
const EmptyHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[800]};
  padding: 8px;
`
const TagGroup = styled.div` margin-bottom: 8px; `
const TagLayerHead = styled.div`
  font-size: 10px;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-weight: 800;
  margin-bottom: 4px;
`
const TagGrid = styled.div` display: flex; flex-wrap: wrap; gap: 4px; `
const TagChip = styled.button`
  font: inherit;
  padding: 3px 9px;
  background: ${({ $active }) => $active ? '#33becc' : '#f4f4f6'};
  color: ${({ $active, theme }) => $active ? '#fff' : theme.colors.haiti};
  border: 1px solid ${({ $active }) => $active ? '#33becc' : '#ececec'};
  border-radius: 999px;
  font-size: 11px;
  cursor: pointer;
`

const UnitPanel = styled(PopoverPanel)` min-width: 240px; max-height: 320px; overflow-y: auto; padding: 8px; `
const PanelActions = styled.div`
  display: inline-flex; gap: 8px;
  padding: 4px 6px 8px;
  border-bottom: 1px dashed #ececec;
  margin-bottom: 6px;
`
const PanelAction = styled.button`
  font: inherit;
  background: none;
  border: none;
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  &:hover { text-decoration: underline; }
`
const UnitList = styled.div` display: flex; flex-direction: column; gap: 2px; `
const UnitRow = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  padding: 4px 6px;
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: #fafbfc; }
`
const UnitCheckbox = styled.span`
  width: 16px; height: 16px;
  border: 1.5px solid ${({ $on }) => $on ? '#33becc' : '#cbcbd2'};
  background: ${({ $on }) => $on ? '#33becc' : 'transparent'};
  border-radius: 4px;
  display: inline-flex; align-items: center; justify-content: center;
`
const UnitLabel = styled.span`
  display: inline-flex; flex-direction: column;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.haiti};
`
const UnitFloor = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const AiBadge = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px;
  background: ${({ $tint }) => `${$tint}1A`};
  border: 1px solid ${({ $tint }) => `${$tint}33`};
  color: ${({ $tint }) => $tint};
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 800;
`
const AiBadgeValue = styled.span` font-variant-numeric: tabular-nums; `
const AiInfo = styled.span`
  display: inline-flex; align-items: center;
  cursor: help;
  opacity: 0.75;
`

const SaveBar = styled.div`
  position: ${({ $inline }) => ($inline ? 'static' : 'sticky')};
  bottom: 24px;
  margin-top: 14px;
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
  padding: 12px 18px;
  background: ${({ theme }) => theme.colors.haiti};
  color: #fff;
  border-radius: 14px;
  box-shadow: ${({ $inline }) => ($inline ? '0 2px 8px rgba(12, 18, 41, 0.12)' : '0 14px 32px rgba(12, 18, 41, 0.30)')};
  animation: ${fadeIn} 240ms ease both;
  z-index: 10;
`
const SaveBarText = styled.span`
  font-size: 13px;
  letter-spacing: 0.02em;
  strong { font-weight: 800; }
`
const SaveBarActions = styled.div` display: inline-flex; gap: 8px; `
const DiscardButton = styled.button`
  font: inherit;
  padding: 8px 14px;
  background: transparent;
  border: 1px solid rgba(255,255,255,0.32);
  color: rgba(255,255,255,0.86);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.08); }
`
const SaveButton = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #33becc 0%, #2aa6b3 100%);
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  &:hover:not(:disabled) { filter: brightness(1.06); }
  &:disabled { opacity: 0.65; cursor: wait; }
`
